import { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from "react-toastify";
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';

// @mui
import { styled } from '@mui/material/styles';
import { Typography, Container } from '@mui/material';
import config from "../../config";
import { encryptData } from '../../utils/cryptoUtils';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/js/bootstrap.bundle';
import '../common.scss';
import * as reportActions from "../../actions/reportActions";
import * as userActions from "../../actions/userActions";
import Loader from "../../components/Loader";
import AccessDenied from '../common/AccessDenied';

// ----------------------------------------------------------------------

const StyledContent = styled('div')(({ theme }) => ({
    maxWidth: 480,
    margin: 'auto',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

const ContactsReportPage = (props) => {
    const { actions } = props;
    const navigate = useNavigate();

    // State management
    const [permissions, setPermissions] = useState([]);
    const [userHasPermission, setUserHasPermission] = useState(true);
    const [contactsData, setContactsData] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [keyword, setKeyword] = useState('');
    const [loansOnly, setLoansOnly] = useState(false);
    const [preloaderStatus, setPreloaderStatus] = useState(false);
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const [reloadTick, setReloadTick] = useState(0);
    const requestIdRef = useRef(0);

    const RECORDS_PER_PAGE = 100;
    const isInitialKeywordMount = useRef(true);
    const isInitialLoansOnlyMount = useRef(true);

    // Check authentication on mount
    useEffect(() => {
        const authToken = localStorage.getItem('authToken');
        if (authToken === null) {
            navigate('/login');
        }
    }, [navigate]);

    // Check permissions
    useEffect(() => {
        const obj = { 'start': '0', 'length': config.MAX_RECORDS };
        const requestData = encryptData(obj);

/*
        const response = actions.getUserModuleData(requestData);
        response.then((result) => {
            if (result.data.success === 1) {
                const permissionsData = result.data.data;
                setPermissions(permissionsData);
                const hasViewPermissions = permissionsData.find(
                    permission => permission.module_name === 'Reports'
                )?.view_permission;
                setUserHasPermission(hasViewPermissions);
            }
        }).catch((error) => {
            console.error("Permission check error:", error);
        }); */
    }, [actions]);

     // Fetch contacts data
    const fetchContactsData = (page = 1) => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        setPreloaderStatus(true);
        const start = (page - 1) * RECORDS_PER_PAGE;

        const params = {
            'start': start.toString(),
            'length': RECORDS_PER_PAGE.toString(),
            'keyword': debouncedKeyword,
            'loans_only': loansOnly ? '1' : '0'
        };

        const requestData = encryptData(params);
        const response = actions.getContactsReportData(requestData, false);

        response.then((result) => {
            if (requestId !== requestIdRef.current) return; // purani request ka response ignore
            setPreloaderStatus(false);
            if (result.data.success === 1) {
                setContactsData(result.data.data || []);
                setTotal(result.data.recordsTotal || 0);
            } else {
                toast.error(result.data.message || 'Failed to fetch contacts data', {
                    position: "top-right",
                });
            }
        }).catch((error) => {
            if (requestId !== requestIdRef.current) return;
            setPreloaderStatus(false);
            toast.error('Error fetching contacts data', {
                position: "top-right",
            });
            console.error("Fetch error:", error);
        });
    };

    // Keyword debounce: 500ms ruko, aur 3+ chars (ya empty) hone par hi apply karo
    useEffect(() => {
        if (keyword.length !== 0 && keyword.length < 3) return undefined;
        const t = setTimeout(() => {
            if (keyword !== debouncedKeyword) {
                setDebouncedKeyword(keyword);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(t);
    }, [keyword]);

    // Ek hi effect se fetch (page / keyword / loansOnly / manual search)
    useEffect(() => {
        fetchContactsData(currentPage);
    }, [currentPage, debouncedKeyword, loansOnly, reloadTick]);

    // Handle search (button)
    const handleSearch = () => {
        setDebouncedKeyword(keyword);
        setCurrentPage(1);
        setReloadTick(t => t + 1);
    };

    // Handle keyword change
    const handleKeywordChange = (event) => {
        setKeyword(event.target.value);
    };

    // Handle loans only checkbox
    const handleLoansOnlyChange = (event) => {
        setLoansOnly(event.target.checked);
        setCurrentPage(1);
    };

    // Export to Excel
    const exportToExcel = () => {
        if (contactsData.length === 0) {
            toast.warning('No data to export', {
                position: "top-right",
            });
            return;
        }

        const exportData = contactsData.map((contact, index) => ({
            'Sr. No.': index + 1,
            'Contact ID': `0000${contact.id}` || '',
            'Name': contact.name || '',
            'Mobile': contact.mobile || '',
            'PAN': contact.pan || '',
            'Applicant Count': contact.applicant_count || 0,
            'Applicant Lead IDs': (contact.applicant_lead_ids || []).join(', '),
            'Co-Applicant Count': contact.co_applicant_count || 0,
            'Co-Applicant Lead IDs': (contact.co_applicant_lead_ids || []).join(', '),
            'Guarantor Count': contact.guarantor_count || 0,
            'Guarantor Lead IDs': (contact.guarantor_lead_ids || []).join(', '),
            'Created At': contact.created_at || '',
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Contacts Report");
        XLSX.writeFile(wb, `Contacts_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    };
// useEffect(() => {
//    console.table(contactsData);
// }, [contactsData]);
    // DataTable columns
    const columns = [
        // {
        //     name: 'Sr. No.',
        //     selector: (row, index) => ((currentPage - 1) * RECORDS_PER_PAGE) + index + 1,
        //     sortable: false,
        //     width: '80px',
        // },
        {
            name: 'Contact ID',
            selector: row => `0000${row.id}`,
            sortable: true,
            width: '110px',
        },
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
            wrap: true,
            width: '200px',
        },
        {
            name: 'Loan #',
            cell: row => {
                // Each agreement number lines up positionally with the
                // applicant_lead_ids array, so pair them by index and
                // make every agreement number a clickable link to that
                // applicant's edit-lead page.
                const agreementNumbers = row.agreement_numbers || [];
                const leadIds = row.agreement_lead_ids || [];
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {agreementNumbers.length > 0 ? (
                            agreementNumbers.map((agreementNo, idx) => {
                                const leadId = leadIds[idx];
                                return leadId ? (
                                    <Link
                                        key={idx}
                                        to={`/dashboard/edit-lead/${btoa(leadId)}`}
                                        style={{
                                            color: '#1976d2',
                                            textDecoration: 'none',
                                            padding: '2px 5px',
                                            borderRadius: '3px',
                                            backgroundColor: '#e3f2fd'
                                        }}
                                    >
                                        {agreementNo}
                                    </Link>
                                ) : (
                                    <span key={idx}>{agreementNo}</span>
                                );
                            })
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                );
            },
            sortable: true,
            wrap: true,
            width: '200px',
        },
        {
            name: 'Mobile',
            selector: row => row.mobile,
            sortable: true,
         },
        
        {
            name: 'PAN',
            selector: row => row.pan ? row.pan.replace(/"/g, '') : '',
            sortable: true,
         },
        
        {
            name: 'Applicant',
            cell: row => {
                const leadIds = row.applicant_lead_ids || [];
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {leadIds.length > 0 ? (
                            leadIds.map((leadId, idx) => (
                                <Link
                                    key={idx}
                                    to={`/dashboard/edit-lead/${btoa(leadId)}`}
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        padding: '2px 5px',
                                        borderRadius: '3px',
                                        backgroundColor: '#e3f2fd'
                                    }}
                                >
                                    {leadId}
                                </Link>
                            ))
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                );
            },
            sortable: true,
            width: '150px',
         },
        {
            name: 'Co-Applicant',
            cell: row => {
                const leadIds = row.co_applicant_lead_ids || [];
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {leadIds.length > 0 ? (
                            leadIds.map((leadId, idx) => (
                                <Link
                                    key={idx}
                                    to={`/dashboard/edit-lead/${btoa(leadId)}`}
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        padding: '2px 5px',
                                        borderRadius: '3px',
                                        backgroundColor: '#e3f2fd'
                                    }}
                                >
                                    {leadId}
                                </Link>
                            ))
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                );
            },
            sortable: true,
            width: '150px',
         },
        {
            name: 'Guarantor',
            cell: row => {
                const leadIds = row.guarantor_lead_ids || [];
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {leadIds.length > 0 ? (
                            leadIds.map((leadId, idx) => (
                                <Link
                                    key={idx}
                                    to={`/dashboard/edit-lead/${btoa(leadId)}`}
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        padding: '2px 5px',
                                        borderRadius: '3px',
                                        backgroundColor: '#e3f2fd'
                                    }}
                                >
                                    {leadId}
                                </Link>
                            ))
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                );
            },
            sortable: true,
            width: '150px',
         },
        
    ];

    // Pagination
    const totalPages = Math.ceil(total / RECORDS_PER_PAGE);
    const startIndex = (currentPage - 1) * RECORDS_PER_PAGE;
    const endIndex = Math.min(startIndex + RECORDS_PER_PAGE, total);

    return (
        <>
            <Helmet>
                <title>Contacts Report | LMS</title>
            </Helmet>

            <Container>
                {userHasPermission ? (
                    <>
                        <div className="container-fluid px-4">
                            <div className="row">
                                <div className="col-md-12">
                                    <Typography variant="h4" className="mb-4">
                                        Contacts Report
                                    </Typography>
                                </div>
                            </div>

                            {/* Filters Section */}
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="keyword">Keyword</label>
                                        <input
                                            type="text"
                                            id="keyword"
                                            className="form-control"
                                            placeholder="Search by name, mobile, email, PAN..."
                                            value={keyword}
                                            onChange={handleKeywordChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="form-group">
                                        <label>&nbsp;</label>
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                id="loansOnly"
                                                className="form-check-input"
                                                checked={loansOnly}
                                                onChange={handleLoansOnlyChange}
                                            />
                                            <label className="form-check-label" htmlFor="loansOnly">
                                                Loans Only
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-2">
                                    <label>&nbsp;</label>
                                    <button
                                        type="button"
                                        className="btn btn-primary w-100"
                                        onClick={handleSearch}
                                    >
                                        Search
                                    </button>
                                </div>
                                <div className="col-md-2">
                                    <label>&nbsp;</label>
                                    <button
                                        type="button"
                                        className="btn btn-success w-100"
                                        onClick={exportToExcel}
                                        disabled={contactsData.length === 0}
                                    >
                                        Export to Excel
                                    </button>
                                </div>
                            </div>

                            {/* Data Table */}
                            <div className="row">
                                <div className="col-md-12">
                                    {preloaderStatus ? (
                                        <Loader />
                                    ) : (
                                        <>
                                            <div className="table-responsive">
                                                <DataTable
                                                    columns={columns}
                                                    data={contactsData}
                                                    pagination={false}
                                                    highlightOnHover
                                                    striped
                                                    dense
                                                />
                                            </div>

                                            {/* Custom Pagination */}
                                            <div className="row mt-3">
                                                <div className="col-md-6">
                                                    <p>
                                                        Showing {startIndex + 1} to {endIndex} of {total} entries
                                                    </p>
                                                </div>
                                                <div className="col-md-6">
                                                    <nav>
                                                        <ul className="pagination justify-content-end">
                                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => setCurrentPage(1)}
                                                                    disabled={currentPage === 1}
                                                                >
                                                                    First
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => setCurrentPage(currentPage - 1)}
                                                                    disabled={currentPage === 1}
                                                                >
                                                                    Previous
                                                                </button>
                                                            </li>
                                                            <li className="page-item active">
                                                                <span className="page-link">
                                                                    {currentPage} of {totalPages}
                                                                </span>
                                                            </li>
                                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => setCurrentPage(currentPage + 1)}
                                                                    disabled={currentPage === totalPages}
                                                                >
                                                                    Next
                                                                </button>
                                                            </li>
                                                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => setCurrentPage(totalPages)}
                                                                    disabled={currentPage === totalPages}
                                                                >
                                                                    Last
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <AccessDenied />
                )}
            </Container>
        </>
    );
};
 
function mapStateToProps(state) {
  return {
     usersData: state.usersData,
      getUserModuleData: state.getUserModuleData,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(Object.assign(userActions.default, reportActions.default), dispatch),
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(ContactsReportPage); 
